import React, { useEffect, useMemo, useRef } from "react";

import { Illustration } from "@orgmym/leeloo-core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { ChatTabType } from "@/features/chat/domain/repositories/chat";

import type { Chat } from "../../types/chat/chat";
import { ChatItemSkeleton } from "../chat-item-skeleton";
import { ChatListItem } from "../chat-list-item";
import { ScrollableTabs } from "../scrollable-tabs";
import * as styles from "./chat-list.css";

const SCROLL_THRESHOLD_PX = 200;
// Throttle the scroll event to avoid multiple calls API
const SCROLL_THROTTLE_MS = 100;
const SKELETON_COUNT_LOAD_MORE = 6;
// Filter functions for each tab to avoid re-rendering when the tab changes
const TAB_FILTERS: Record<string, (chat: Chat) => boolean> = {
  unread: (chat) => !chat.is_read,
  to_deliver: (chat) => chat.to_deliver != null && chat.to_deliver > 0,
  pending_request: (chat) =>
    chat.media_request != null && chat.media_request > 0,
  blocked: (chat) => chat.user.is_blacklisted === true,
  muted: (chat) => chat.user.is_muted === true
};

type ChatTabCounts = {
  all: number;
  unread: number;
  to_deliver: number;
  pending_request: number;
  blocked: number;
  muted: number;
};

type TabConfig = {
  id: ChatTabType;
  translationKey: string;
  defaultLabel: string;
  countKey: keyof ChatTabCounts;
  showLoading?: boolean;
};

const TAB_CONFIGS: TabConfig[] = [
  {
    id: "all",
    translationKey: "creators_messaging_messages_list_filters_all",
    defaultLabel: "All",
    countKey: "all"
  },
  {
    id: "unread",
    translationKey: "creators_messaging_messages_list_filters_unread",
    defaultLabel: "Unread",
    countKey: "unread",
    showLoading: true
  },
  {
    id: "to_deliver",
    translationKey: "creators_messaging_messages_list_filters_to_deliver",
    defaultLabel: "To delivered",
    countKey: "to_deliver",
    showLoading: true
  },
  {
    id: "pending_request",
    translationKey: "creators_messaging_messages_list_filters_pending_request",
    defaultLabel: "Pending requests",
    countKey: "pending_request",
    showLoading: true
  },
  {
    id: "blocked",
    translationKey: "creators_messaging_messages_list_filters_blocked",
    defaultLabel: "Blocked",
    countKey: "blocked",
    showLoading: true
  },
  {
    id: "muted",
    translationKey: "creators_messaging_messages_list_filters_muted",
    defaultLabel: "Muted",
    countKey: "muted",
    showLoading: true
  }
];

interface ChatListProps {
  chats: Chat[];
  activeTab: ChatTabType;
  onTabChange: (tab: ChatTabType) => void;
  onChatSelect?: (chat: Chat) => void;
  selectedChatId?: string;
  selectedChat?: Chat;
  loading?: boolean;
  isLoadingMore?: boolean;
  totalToLoad?: number;
  className?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  tabCounts?: ChatTabCounts;
  isLoadingStatusCounts?: boolean;
  hasOpenChat?: boolean;
  hasSearchQuery?: boolean;
}

export const ChatList: React.FC<ChatListProps> = ({
  chats,
  activeTab,
  onTabChange,
  onChatSelect,
  selectedChatId,
  selectedChat: selectedChatProp,
  loading = false,
  isLoadingMore = false,
  totalToLoad = 6,
  className = "",
  onLoadMore,
  hasMore = false,
  tabCounts: tabCountsProp,
  isLoadingStatusCounts = false,
  hasOpenChat = false,
  hasSearchQuery = false
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  const selectedChat = useMemo(() => {
    return (
      selectedChatProp ||
      (selectedChatId
        ? chats.find((chat) => chat.id === selectedChatId)
        : undefined)
    );
  }, [selectedChatProp, selectedChatId, chats]);

  const filteredChats = useMemo(() => {
    let filtered = chats.filter((chat) => chat.user);

    const CLIENT_SIDE_TABS = ["unread", "disabled"];
    if (activeTab !== "all" && CLIENT_SIDE_TABS.includes(activeTab)) {
      const tabFilter = TAB_FILTERS[activeTab];
      if (tabFilter) {
        filtered = filtered.filter(tabFilter);
      }
    }

    if (selectedChat && !filtered.find((c) => c.id === selectedChat.id)) {
      filtered = [...filtered, selectedChat];
    }

    return filtered;
  }, [chats, activeTab, selectedChat]);

  const tabCounts = useMemo<ChatTabCounts>(() => {
    if (tabCountsProp) {
      return tabCountsProp;
    }

    const unreadCount = chats.filter((chat) => !chat.is_read).length;
    const toDeliverCount = chats.filter(
      (chat) => chat.to_deliver != null && chat.to_deliver > 0
    ).length;
    const pendingRequestCount = chats.filter(
      (chat) => chat.media_request != null && chat.media_request > 0
    ).length;
    const blockedCount = chats.filter(
      (chat) => chat.user.is_blacklisted === true
    ).length;
    const mutedCount = chats.filter(
      (chat) => chat.user.is_muted === true
    ).length;

    return {
      all: chats.length,
      unread: unreadCount,
      to_deliver: toDeliverCount,
      pending_request: pendingRequestCount,
      blocked: blockedCount,
      muted: mutedCount
    };
  }, [chats, tabCountsProp]);

  const isCountsLoading = isLoadingStatusCounts && !tabCountsProp;

  const handleChatClick = (chat: Chat) => {
    onChatSelect?.(chat);
  };

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || !onLoadMore || !hasMore || isLoadingMore) return;

    let throttleTimeout: NodeJS.Timeout | null = null;

    // Load more immediately if content doesn't fill the viewport
    if (contentElement.scrollHeight <= contentElement.clientHeight) {
      onLoadMore();
    }

    const handleScroll = () => {
      if (throttleTimeout) return;

      throttleTimeout = setTimeout(() => {
        throttleTimeout = null;
        const { scrollTop, scrollHeight, clientHeight } = contentElement;
        if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
          onLoadMore();
        }
      }, SCROLL_THROTTLE_MS);
    };

    contentElement.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
      if (throttleTimeout) clearTimeout(throttleTimeout);
    };
  }, [onLoadMore, hasMore, isLoadingMore]);

  return (
    <div className={clsx(styles.chatList, className)} data-testid="chat-list">
      <div className={styles.tabs}>
        <ScrollableTabs
          activeTab={activeTab}
          onTabChange={onTabChange}
          tabs={TAB_CONFIGS.map((config) => ({
            id: config.id,
            label: t(config.translationKey, config.defaultLabel),
            count: tabCounts[config.countKey],
            isLoading: config.showLoading ? isCountsLoading : undefined
          }))}
        />
      </div>

      <div
        ref={contentRef}
        className={styles.content}
        data-testid="chat-list-content"
      >
        {filteredChats.length === 0 && !loading ? (
          <div className={styles.emptyList} data-testid="chat-list-empty">
            <Illustration name="empty-state" />
            {hasSearchQuery
              ? t(
                  "creators_messaging_messages_list_search_bar_wrong_request_description"
                )
              : t(
                  "creators_messaging_messages_list_search_bar_empty_list_description"
                )}
          </div>
        ) : (
          <div className={styles.items} data-testid="chat-list-items">
            {filteredChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                isSelected={selectedChatId === chat.id}
                onSelect={handleChatClick}
                activeTab={activeTab}
              />
            ))}

            {/* Display skeletons for items still loading */}
            {loading &&
              filteredChats.length === 0 &&
              Array.from({ length: totalToLoad }).map((_, index) => (
                <ChatItemSkeleton
                  key={`skeleton-${index}`}
                  data-testid={`chat-item-skeleton-${index}`}
                />
              ))}

            {/* Display skeletons when loading more (infinite scroll) */}
            {isLoadingMore &&
              Array.from({ length: SKELETON_COUNT_LOAD_MORE }).map(
                (_, index) => (
                  <ChatItemSkeleton
                    key={`skeleton-more-${index}`}
                    data-testid={`chat-item-skeleton-more-${index}`}
                  />
                )
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
